import { useCallback, useMemo, useState } from 'react'
import { useOrg } from '@/admin/hooks/use-org'
import { TABLE_PREFERENCES_CHANGED_EVENT } from '@/admin/hooks/use-table-column-preferences'
import type { ProductFilterValue } from '@/shared/components/ui/product-filter-menu'

/**
 * Persist a table's filter fly-out selection to localStorage, partitioned per
 * tenant + table, so the selected facets survive a page refresh.
 *
 * Mirrors ``useTableColumnPreferences`` (same storage discipline: refuse to
 * read/write without a tenant slug to avoid cross-tenant bleed). The value is
 * a ``ProductFilterValue`` (facet key -> selected option values), the same
 * shape ``ProductFilterMenu`` consumes.
 */

const KEY_PREFIX = 'raven:table-filter-preferences:v1'

function getStorageKey(tableId: string, tenantSlug: string | null): string | null {
  if (!tenantSlug) return null
  return `${KEY_PREFIX}:${tenantSlug}:${tableId}`
}

function readFilterValues(storageKey: string | null): ProductFilterValue {
  if (typeof window === 'undefined' || !storageKey) return {}
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    // Keep only string[] entries so a corrupt payload can't poison the params.
    const clean: ProductFilterValue = {}
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
        clean[key] = value as string[]
      }
    }
    return clean
  } catch {
    return {}
  }
}

function writeFilterValues(storageKey: string | null, values: ProductFilterValue) {
  if (typeof window === 'undefined' || !storageKey) return
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(values))
    window.dispatchEvent(new Event(TABLE_PREFERENCES_CHANGED_EVENT))
  } catch {
    // Ignore storage failures so the filter keeps working in private mode.
  }
}

/**
 * Read / write a table's saved filter selection outside React - used to
 * capture and restore it as part of a saved dashboard view.
 */
export function readTableFilterPreferences(
  tableId: string,
  tenantSlug: string | null,
): ProductFilterValue {
  return readFilterValues(getStorageKey(tableId, tenantSlug))
}

export function writeTableFilterPreferences(
  tableId: string,
  tenantSlug: string | null,
  values: ProductFilterValue,
) {
  writeFilterValues(getStorageKey(tableId, tenantSlug), values)
}

export interface TableFilterPreferenceControls {
  /** Show a selection without saving it as the user's own (an open saved view). */
  applyFilterValues: (values: ProductFilterValue) => void
  /** Go back to the user's own saved selection (a saved view was closed). */
  restoreSavedFilterValues: () => void
}

export function useTableFilterPreferences(
  tableId: string,
): [
  ProductFilterValue,
  (values: ProductFilterValue) => void,
  TableFilterPreferenceControls,
] {
  const { data: org } = useOrg()
  const tenantSlug = org?.organisation?.tenant_slug ?? null

  const storageKey = useMemo(
    () => getStorageKey(tableId, tenantSlug),
    [tableId, tenantSlug],
  )

  const [filterValues, setFilterValuesState] = useState<ProductFilterValue>(() =>
    readFilterValues(storageKey),
  )

  // Re-hydrate during render when the tenant/table key changes.
  const [prevStorageKey, setPrevStorageKey] = useState(storageKey)
  if (storageKey !== prevStorageKey) {
    setPrevStorageKey(storageKey)
    setFilterValuesState(readFilterValues(storageKey))
  }

  const setFilterValues = useCallback(
    (values: ProductFilterValue) => {
      setFilterValuesState(values)
      writeFilterValues(storageKey, values)
    },
    [storageKey],
  )

  const controls = useMemo<TableFilterPreferenceControls>(
    () => ({
      applyFilterValues: setFilterValuesState,
      restoreSavedFilterValues: () => setFilterValuesState(readFilterValues(storageKey)),
    }),
    [storageKey],
  )

  return [filterValues, setFilterValues, controls]
}
